import { type __InputStylesNames, Group, Text, type TextInputProps } from '@mantine/core'
import type { ReactElement } from 'react'
import { cloneElement, useMemo } from 'react'

import { AISplitButton } from '@/components/AISplitButton'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/AIInputWrapper.css'

type AIFieldState = {
  state: 'idle' | 'loading' | 'pending'
  suggestion: string | null
}

type AIInputWrapperProps = {
  fieldState: AIFieldState
  children: ReactElement<TextInputProps>
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  disabled?: boolean
}

function useStatusDisplay(_fieldState: AIFieldState) {
  return useMemo(
    () => ({
      statusText: '',
    }),
    [],
  )
}

function useInputModification(originalProps: TextInputProps, fieldState: AIFieldState, onReject: () => void) {
  return useMemo(() => {
    const { state, suggestion } = fieldState
    const isPending = state === 'pending'
    const isLoading = state === 'loading'
    const isAIActive = isPending || isLoading
    const classNames = originalProps.classNames as Partial<Record<__InputStylesNames, string>>

    return {
      ...originalProps,
      // Auto-reject suggestion when user starts typing
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isPending) {
          onReject()
        }
        originalProps.onChange?.(e)
      },
      value: isPending ? (suggestion ?? '') : (originalProps.value ?? ''),
      withAsterisk: false,
      variant: isAIActive ? 'filled' : originalProps.variant,
      classNames: {
        ...originalProps.classNames,
        input: theme.cx(classNames?.input, isAIActive && classes.input),
      },
      error: originalProps.error,
    }
  }, [originalProps, fieldState, onReject])
}

// Action buttons component
function ActionButtons({
  fieldState,
  onAskSidekick,
  onAccept,
  onReject,
  onRegenerate,
  disabled,
  statusText,
}: {
  fieldState: AIFieldState
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  disabled?: boolean
  statusText: string
}) {
  return (
    <Group align="center" gap="xs">
      {statusText && (
        <Text fw={600} tt="uppercase" size="sm" variant="gradient">
          {statusText}
        </Text>
      )}
      <AISplitButton
        fieldState={fieldState.state}
        onAskSidekick={onAskSidekick}
        onAccept={onAccept}
        onReject={onReject}
        onRegenerate={onRegenerate}
        disabled={disabled}
      />
    </Group>
  )
}

export function AIInputWrapper(props: AIInputWrapperProps) {
  const { fieldState, onAskSidekick, onAccept, onReject, onRegenerate, disabled, children } = props

  // Early return if disabled - no modifications needed
  if (disabled) return children

  const statusDisplay = useStatusDisplay(fieldState)
  const modifiedInputProps = useInputModification(children.props, fieldState, onReject)

  const enhancedProps: TextInputProps = {
    ...modifiedInputProps,
    label: (
      <Group justify="space-between" align="center">
        <InputLabel label={children.props.label} withAsterisk={children.props.withAsterisk} withSpacer={false} />

        <ActionButtons
          fieldState={fieldState}
          onAskSidekick={onAskSidekick}
          onAccept={onAccept}
          onReject={onReject}
          onRegenerate={onRegenerate}
          disabled={disabled}
          statusText={statusDisplay.statusText}
        />
      </Group>
    ),
  }

  return cloneElement(children, enhancedProps)
}
